/** @prettier */

import {memo, useEffect, useState} from 'react';
import {Card, CardContent, Grid, Stack, Box} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import BarChartGraph from '../../StackBarChart';
import LinearWithValueLabel from '../../LinearProgressWithLabel';
import {StaticDatePicker, StaticComparisonDatePicker} from './DatePicker';
import PolygonalLineGraph from '../../PolygonalLineGraph';
import ItemGraphDialogChiller from '../../ItemGraphDialogChiller';
import ItemGraphDialogHyozan from '../../ItemGraphDialogHyozan';
import ItemGraphDialogQton from '../../ItemGraphDialogQton';
import BarLineMixedChartGraph from '../../BarLineMixedChart';
import ChangeUnitGraphDialog from '../../ChangeUnitGraphDialog';
import TextFieldSelectBox from '../../SelectBox';
import {PowerTrendSelectButton} from './molculate/PowerTrendButton';
import {AddButton, PrimaryButton} from '../../BasicButton';
//interface import
import * as apiIF from '../../../Type/api';
//API import
import {getSettingUser} from '../../../API/API_Collection';
//type import
import {GraphFormType, GraphDataRange, graphProps} from '../../../Type/TypeDef';
//Style import
import '../../../css/common/common.css';

import {useGetStartEndDate} from '../../../hooks/graph/useGetStartEndDate';
import {makeRTGraphData} from '../../../functions/makeRTGraphData';
import {useRTChangeStartEndDate} from '../../../hooks/graph/useQtonChangeStartEndDate';
import {pullDownMenu} from '../../../define';
import {makeXLabel} from '../../../functions/graphFunction';

/**
 * 熱源機グラフ
 */
const RTGraph = memo((props: {graphProps: graphProps}) => {
  const trans = useTranslation().t; //翻訳関数
  //prettier-ignore
  const {siteID, graphTitle, graphTarget, graphTargetID, graphType, childUnitInfo, setGraphParam} = props.graphProps;

  //日付の表示（1日/1週間/1か月/1年/10年）
  const [dateRange, setDateRange] = useState<GraphDataRange>('day');
  //prettier-ignore
  const [barGraphDataHot, setBarGraphDataHot] = useState<ApexAxisChartSeries['0']['data']>([]); //暖房消費電力グラフデータ
  //prettier-ignore
  const [comparisonBarGraphDataHot, setComparisonBarGraphDataHot] = useState<ApexAxisChartSeries['0']['data']>([]); //暖房消費電力データ（比較日）
  //prettier-ignore
  const [barGraphDataCool, setBarGraphDataCool] = useState<ApexAxisChartSeries['0']['data']>([]); //冷房消費電力グラフデータ
  //prettier-ignore
  const [comparisonBarGraphDataCool, setComparisonBarGraphDataCool] = useState<ApexAxisChartSeries['0']['data']>([]); //冷房消費電力データ（比較日）
  //prettier-ignore
  const [energyGraphData, setEnergyGraphData] = useState<ApexAxisChartSeries['0']['data']>([]); //熱量グラフデータ
  //prettier-ignore
  const [comparisonEnergyGraphData, setComparisonEnergyGraphData] = useState<ApexAxisChartSeries['0']['data']>([]); //熱量グラフデータ（比較日）
  //prettier-ignore
  const [copGraphData, setCopGraphData] = useState<ApexAxisChartSeries['0']['data']>([]); //COPグラフデータ
  //prettier-ignore
  const [comparisonCopEnergyGraphData, setComparisonCopGraphData] = useState<ApexAxisChartSeries['0']['data']>([]); //COPグラフデータ（比較日）
  //prettier-ignore
  const [lineGraph, setLineGraph] = useState<{name: string; data: ApexAxisChartSeries['0']['data']}[]>([]); //折れ線グラフデータ
  //prettier-ignore
  const [comparisonLineGraph, setComparisonLineGraph] = useState<{name: string; data: ApexAxisChartSeries['0']['data']}[]>([]); //折れ線グラフデータ（比較日）
  const [xDate, setXDate] = useState<string[] | []>([]); //通常日の日付データ
  const [comparisonXDate, setComparisonXDate] = useState<string[]>([]); //比較日の日付データ
  const [xAxisLabel, setXAxisLabel] = useState<string[]>([]); //x軸に表示するラベル
  //prettier-ignore
  const [comparisonXAxisLabel, setComparisonXAxisLabel] = useState<string[]>([]); //比較グラフのx軸に表示するラベル
  const [yTitle, setYTitle] = useState(
    graphTarget === 'qton'
      ? `${trans('RTGraph.line.yAxisTitle.temp')}`
      : `${trans('RTGraph.line.yAxisTitle.pressure.mpa')}`
  ); //Y軸タイトル（折れ線グラフ用）
  /** Q-tonグラフの初期表示項目 */
  const selectedItemInitQton: (keyof apiIF.resParameters.rtOUStates)[] = [
    'outlet-temperature',
    'inlet-temperature',
    'tank-sensor-temperature',
  ];
  /** Hyozanグラフの初期表示項目 */
  const selectedItemInitHyozan: (keyof apiIF.resParameters.rtOUStates)[] = [
    'high-pressure',
    'low-pressure',
  ];
  /** Chillerグラフの初期表示項目 */
  const selectedItemInitChiller: (keyof apiIF.resParameters.rtOUStates)[] = [
    'inlet-water-temperature',
    'outlet-water-temperature',
    'flow-rate',
  ];
  const [selectedItem, setSelectedItem] = useState<
    (keyof apiIF.resParameters.rtOUStates)[]
  >(
    (() => {
      switch (graphTarget) {
        case 'qton':
          return selectedItemInitQton;
        case 'hyozan':
          return selectedItemInitHyozan;
        case 'chiller':
          return selectedItemInitChiller;
        default:
          return selectedItemInitQton;
      }
    })()
  ); //グラフの表示項目
  const [isDialogOpen, setIsDialogOpen] = useState(false); //表示項目選択ダイアログ表示
  const [isUnitSelectDialogOpen, setIsUnitSelectDialogOpen] = useState(false); //親機子機選択ダイアログ表示
  const [graphLegend, setGraphLegend] = useState<string[]>([]);
  const [selectGraphTargetID, setSelectGraphTargetID] = useState(graphTargetID); //グラフに表示するユニットのID
  //prettier-ignore
  const [selectCsvTitleNameTarget, setSelectCsvTitleNameTarget] = useState(graphTitle); //CSVファイルやグラフ画面に表示するユニット名
  const [activeComparison, setActiveComparison] = useState(false); //比較データ表示判定

  //prettier-ignore
  //グラフに表示する日付の範囲を取得
  const {timeZone, graphStartDate, graphEndDate, comparisonGraphStartDate, comparisonGraphEndDate, setGraphDispDate, setComparisonGraphDispDate} = useGetStartEndDate(siteID);

  //日付や対象が変更されたとき、グラフに描画するデータを再取得
  const {rtStateData, csvFileNameDate, graphDispError, apiWait} =
    useRTChangeStartEndDate({
      siteID: siteID,
      graphStartDate: graphStartDate,
      graphEndDate: graphEndDate,
      graphType: graphType,
      timeZone: timeZone,
      dateRange: dateRange,
      selectedItem: selectedItem,
    });
  const {
    rtStateData: comparisonRtStateData,
    csvFileNameDate: comparisonCsvFileNameDate,
    graphDispError: comparisonGraphDispError,
    apiWait: comparisonApiWait,
  } = useRTChangeStartEndDate({
    siteID: siteID,
    graphStartDate: comparisonGraphStartDate,
    graphEndDate: comparisonGraphEndDate,
    graphType: graphType,
    timeZone: timeZone,
    dateRange: dateRange,
    selectedItem: selectedItem,
  });

  //受信APIデータ変更時、グラフ表示項目変更時にグラフ描画用データを作成する
  useEffect(() => {
    if (rtStateData === undefined) return;

    //prettier-ignore
    /** Q-tonグラフ表示項目名テーブル */
    const itemNameTableQton: {
      [fiapID: string]: string; //fiapID: 表示名
    } = {
      'outlet-temperature'           : trans('RTGraph.line.graphItem.outletTemp'), //出湯温度
      'inlet-temperature'            : trans('RTGraph.line.graphItem.inletTemp'), //入水温度
      'tank-sensor-temperature'      : trans('RTGraph.line.graphItem.tankSensorTemp'), //貯湯温度
      'target-temperature-to-top-up' : trans('RTGraph.line.graphItem.targetOutletTemp'), //目標出湯温度　target-temperature-to-top-upをTarget outlet temperatureと表示する
      'target-inlet-temperature'      : trans('RTGraph.line.graphItem.targetInletTemp'), //目標入水
      'remote-control-preset-temperature' : trans('RTGraph.line.graphItem.remoteControlPresetTemp'), //リモコン設定温度
      'outdoor-temperature'               : trans('RTGraph.line.graphItem.outdoorTemp'), //室外温度
      'target-hot-water-amount'      : trans('RTGraph.line.graphItem.targetHotWaterAmount'), //目標貯湯量
      'hot-water-amount'             : trans('RTGraph.line.graphItem.hotWaterAmount'), //貯湯量
      'high-pressure'                : trans('RTGraph.line.graphItem.compressorHighPressure'), //コンプ高圧
      'low-pressure'                 : trans('RTGraph.line.graphItem.compressorLowPressure'), //コンプ低圧
    };
    //prettier-ignore
    /** Hyozanグラフ表示項目名テーブル */
    const itemNameTableHyozan: {
      [fiapID: string]: string; //fiapID: 表示名
    } = {
      'high-pressure'           : trans('RTGraph.line.graphItem.compressorHighPressure'), //コンプ高圧
      'low-pressure'            : trans('RTGraph.line.graphItem.compressorLowPressure'), //コンプ低圧
      'evaporating-temperature' : trans('RTGraph.line.graphItem.evaporatingTemp'), //蒸発温度
      'target-low-pressure'     : trans('RTGraph.line.graphItem.targetLowPressure'), //目標低圧
    };
    //prettier-ignore
    /** Chillerグラフ表示項目名テーブル */
    const itemNameTableChiller: {
      [fiapID: string]: string; //fiapID: 表示名
    } = {
      'inlet-water-temperature'  : trans('RTGraph.line.graphItem.inletTemp'), //入口水温
      'outlet-water-temperature' : trans('RTGraph.line.graphItem.outletTemp'), //出口水温
      'flow-rate'                : trans('RTGraph.line.graphItem.flowRate'), //水量
      'high-pressure'            : trans('RTGraph.line.graphItem.compressor1HighPressure'), //CM1高圧
      'low-pressure'             : trans('RTGraph.line.graphItem.compressor1LowPressure'), //CM1低圧
      'high-pressure2'           : trans('RTGraph.line.graphItem.compressor2HighPressure'), //CM2高圧
      'low-pressure2'            : trans('RTGraph.line.graphItem.compressor2LowPressure'), //CM2低圧
    };
    const ret = makeRTGraphData({
      graphType: graphType,
      dateRange: dateRange,
      graphTarget: graphTarget,
      graphTargetID: selectGraphTargetID,
      graphStartDate: graphStartDate,
      graphEndDate: graphEndDate,
      timeZone: timeZone,
      rtStateData: rtStateData,
      selectedItem: selectedItem,
      trans,
    });

    //x軸の時間データ
    setXDate(ret.xLabel);
    //折れ線グラフデータ
    if (ret.yArrayLine.length > 0) {
      if (graphTarget === 'qton') {
        setLineGraph(
          ret.yArrayLine.map(value => {
            if (value.name in itemNameTableQton) {
              return {name: itemNameTableQton[value.name], data: value.data};
            } else {
              return {name: value.name, data: value.data};
            }
          })
        );
        setGraphLegend(
          ret.yArrayLine.map(value => {
            return itemNameTableQton[value.name];
          })
        );
      } else if (graphTarget === 'hyozan') {
        setLineGraph(
          ret.yArrayLine.map(value => {
            if (value.name in itemNameTableHyozan) {
              return {name: itemNameTableHyozan[value.name], data: value.data};
            } else {
              return value;
            }
          })
        );
        setGraphLegend(
          ret.yArrayLine.map(value => {
            return itemNameTableHyozan[value.name];
          })
        );
      } else if (graphTarget === 'chiller') {
        setLineGraph(
          ret.yArrayLine.map(value => {
            if (value.name in itemNameTableChiller) {
              return {name: itemNameTableChiller[value.name], data: value.data};
            } else {
              return value;
            }
          })
        );
        setGraphLegend(
          ret.yArrayLine.map(value => {
            return itemNameTableChiller[value.name];
          })
        );
      }
    }
    //暖房消費電力データ
    if (ret.yArrayBarHot.length > 0) {
      setBarGraphDataHot(ret.yArrayBarHot[0].data);
    }
    //冷房消費電力データ
    if (ret.yArrayBarCool.length > 0) {
      setBarGraphDataCool(ret.yArrayBarCool[0].data);
    }
    //熱量データ
    if (ret.yArrayBarEnergy.length > 0) {
      setEnergyGraphData(ret.yArrayBarEnergy[0].data);
    }
    //COPグラフデータ
    if (ret.yArrayCopLine.length > 0) {
      setCopGraphData(ret.yArrayCopLine[0].data);
    }
    //圧力単位からY軸のタイトルを作成
    getSettingUser().then(response => {
      if (graphTarget !== 'qton') {
        if (response['unit'].pressure === 'MPa') {
          setYTitle(`${trans('RTGraph.line.yAxisTitle.pressure.mpa')}`);
        } else {
          setYTitle(`${trans('RTGraph.line.yAxisTitle.pressure.bar')}`);
        }
      }
    });
  }, [trans, rtStateData, selectGraphTargetID, activeComparison]);

  //比較用データの変更時、比較グラフ描画用データを作成する
  useEffect(() => {
    if (comparisonRtStateData === undefined) return;

    //prettier-ignore
    /** Q-tonグラフ表示項目名テーブル（比較日のデータ用） */
    const itemNameTableQtonComparison: {
      [fiapID: string]: string; //fiapID: 表示名
    } = {
      'outlet-temperature'           : trans('RTGraph.line.graphItem.comparisonOutletTemp'), //出湯温度(比較日)
      'inlet-temperature'            : trans('RTGraph.line.graphItem.comparisonInletTemp'), //入水温度(比較日)
      'tank-sensor-temperature'      : trans('RTGraph.line.graphItem.comparisonTankSensorTemp'), //貯湯温度(比較日)
      'target-temperature-to-top-up' : trans('RTGraph.line.graphItem.comparisonTargetOutletTemp'), //目標貯湯温度(比較日)
      'target-inlet-temperature'          : trans('RTGraph.line.graphItem.comparisonTargetInletTemp'), //目標入水温度(比較日)
      'remote-control-preset-temperature' : trans('RTGraph.line.graphItem.comparisonRemoteControlPresetTemp'), //リモコン設定温度(比較日)
      'outdoor-temperature'               : trans('RTGraph.line.graphItem.comparisonOutdoorTemp'), //室外温度(比較日)
      'target-hot-water-amount'      : trans('RTGraph.line.graphItem.comparisonTargetHotWaterAmount'), //目標貯湯量(比較日)
      'hot-water-amount'             : trans('RTGraph.line.graphItem.comparisonHotWaterAmount'), //貯湯量(比較日)
      'high-pressure'                : trans('RTGraph.line.graphItem.comparisonCompressorHighPressure'), //コンプ高圧(比較日)
      'low-pressure'                 : trans('RTGraph.line.graphItem.comparisonCompressorLowPressure'), //コンプ低圧(比較日)
    };
    //prettier-ignore
    /** Hyozanグラフ表示項目名テーブル（比較日のデータ用） */
    const itemNameTableHyozanComparison: {
      [fiapID: string]: string; //fiapID: 表示名
    } = {
      'high-pressure'           : trans('RTGraph.line.graphItem.comparisonCompressorHighPressure'), //コンプ高圧(比較日)
      'low-pressure'            : trans('RTGraph.line.graphItem.comparisonCompressorLowPressure'), //コンプ低圧(比較日)
      'evaporating-temperature' : trans('RTGraph.line.graphItem.comparisonEvaporatingTemp'), //蒸発温度(比較日)
      'target-low-pressure'     : trans('RTGraph.line.graphItem.comparisonTargetLowPressure'), //目標低圧(比較日)
    };
    //prettier-ignore
    /** Chillerグラフ表示項目名テーブル（比較日のデータ用） */
    const itemNameTableChillerComparison: {
      [fiapID: string]: string; //fiapID: 表示名
    } = {
      'inlet-water-temperature'  : trans('RTGraph.line.graphItem.comparisonInletTemp'), //入口水温(比較日)
      'outlet-water-temperature' : trans('RTGraph.line.graphItem.comparisonOutletTemp'), //出口水温(比較日)
      'flow-rate'                : trans('RTGraph.line.graphItem.comparisonFlowRate'), //水量(比較日)
      'high-pressure'            : trans('RTGraph.line.graphItem.comparisonCompressor1HighPressure'), //CM1高圧(比較日)
      'low-pressure'             : trans('RTGraph.line.graphItem.comparisonCompressor1LowPressure'), //CM1低圧(比較日)
      'high-pressure2'           : trans('RTGraph.line.graphItem.comparisonCompressor2HighPressure'), //CM2高圧(比較日)
      'low-pressure2'            : trans('RTGraph.line.graphItem.comparisonCompressor2LowPressure'), //CM2低圧(比較日)
    };
    const comparisonRet = makeRTGraphData({
      graphType: graphType,
      dateRange: dateRange,
      graphTarget: graphTarget,
      graphTargetID: selectGraphTargetID,
      graphStartDate: comparisonGraphStartDate,
      graphEndDate: comparisonGraphEndDate,
      timeZone: timeZone,
      rtStateData: comparisonRtStateData,
      selectedItem: selectedItem,
      trans,
    });

    setComparisonXDate(comparisonRet.xLabel);
    //折れ線グラフデータ
    if (graphTarget === 'qton') {
      setComparisonLineGraph(
        comparisonRet.yArrayLine.map(value => {
          if (value.name in itemNameTableQtonComparison) {
            return {
              name: itemNameTableQtonComparison[value.name],
              data: value.data,
            };
          } else {
            return value;
          }
        })
      );
    } else if (graphTarget === 'hyozan') {
      setComparisonLineGraph(
        comparisonRet.yArrayLine.map(value => {
          if (value.name in itemNameTableHyozanComparison) {
            return {
              name: itemNameTableHyozanComparison[value.name],
              data: value.data,
            };
          } else {
            return value;
          }
        })
      );
    } else if (graphTarget === 'chiller') {
      setComparisonLineGraph(
        comparisonRet.yArrayLine.map(value => {
          if (value.name in itemNameTableChillerComparison) {
            return {
              name: itemNameTableChillerComparison[value.name],
              data: value.data,
            };
          } else {
            return value;
          }
        })
      );
    }

    //暖房消費電力比較データ
    if (comparisonRet.yArrayBarHot.length > 0) {
      setComparisonBarGraphDataHot(comparisonRet.yArrayBarHot[0].data);
    }
    //冷房消費電力比較データ
    if (comparisonRet.yArrayBarCool.length > 0) {
      setComparisonBarGraphDataCool(comparisonRet.yArrayBarCool[0].data);
    }
    //熱量比較データ
    if (comparisonRet.yArrayBarEnergy.length > 0) {
      setComparisonEnergyGraphData(comparisonRet.yArrayBarEnergy[0].data);
    }
    //COPグラフ比較データ
    if (comparisonRet.yArrayCopLine.length > 0) {
      setComparisonCopGraphData(comparisonRet.yArrayCopLine[0].data);
    }
  }, [trans, comparisonRtStateData, selectGraphTargetID, activeComparison]);

  //x軸に表示するラベルを作成する
  useEffect(() => {
    makeXLabel({
      xDate: xDate,
      comparisonXDate: comparisonXDate,
      dateRange: dateRange,
      graphType: graphType,
      activeComparison: activeComparison,
      setXAxisLabel: setXAxisLabel,
      setComparisonXAxisLabel: setComparisonXAxisLabel,
    });
  }, [xDate, comparisonXDate]);

  //データ取得範囲で10年が選択された際に、比較機能をOFFにする
  useEffect(() => {
    if (dateRange === 'tenYear') {
      setActiveComparison(false);
    }
  }, [dateRange]);

  /**
   * グラフパラメータ更新時にグラフへ送るIDと名前を更新
   */
  useEffect(() => {
    setSelectGraphTargetID(graphTargetID);
    setSelectCsvTitleNameTarget(graphTitle);
  }, [props]);

  //表示範囲が1ヵ月間、または1年間の状態で折れ線グラフへ移動した場合、グラフ表示範囲を1日に変更
  if (
    graphType === 'line' &&
    (dateRange === 'month' || dateRange === 'year' || dateRange === 'tenYear')
  ) {
    setDateRange('day');
  }

  //比較機能ONかつ折れ線グラフの選択項目が4つの場合
  if (activeComparison && selectedItem.length === 4) {
    //グラフの選択項目を4個から3個に減らす
    setSelectedItem(
      selectedItem.filter((item, index) => index < selectedItem.length - 1)
    );
  }

  /** 比較機能のON/OFFを切り替える関数 */
  const handleOnComparison = () => {
    setActiveComparison(!activeComparison);
  };

  /**
   * グラフ形式を決定
   * @param type グラフ形式
   */
  const selectPowerTrend = (type: GraphFormType) => {
    setGraphParam(graphTarget, graphTargetID, type);
  };

  /**
   * グラフ
   * @returns JSX.Element
   */
  const GraphDisplay = () => {
    /** グラフアイテムの単位 */
    const graphUnitTable: {
      [viewItem: string]: 'temp' | 'percent' | 'pressureUnit' | 'm3/h';
    } = {
      'outlet-temperature': 'temp',
      'inlet-temperature': 'temp',
      'tank-sensor-temperature': 'temp',
      'target-temperature-to-top-up': 'temp',
      'target-inlet-temperature': 'temp',
      'remote-control-preset-temperature': 'temp',
      'outdoor-temperature': 'temp',
      'high-pressure': 'pressureUnit',
      'low-pressure': 'pressureUnit',
      'high-pressure2': 'pressureUnit',
      'low-pressure2': 'pressureUnit',
      'target-hot-water-amount': 'percent',
      'hot-water-amount': 'percent',
      'evaporating-temperature': 'temp',
      'target-low-pressure': 'pressureUnit',
      'flow-rate': 'm3/h',
      'outlet-water-temperature': 'temp',
      'inlet-water-temperature': 'temp',
    };

    //折れ線グラフの場合
    if (graphType === 'line') {
      //比較状態の場合表示できる項目は3つまでとなるため、4項目目のデータと凡例は削除する。
      if (activeComparison && graphLegend.length > 3) {
        graphLegend.pop();
        lineGraph.pop();
      }

      /** グラフ単位一覧 */
      const graphUnits = selectedItem.map(item => {
        return graphUnitTable[item];
      });
      //比較状態の場合表示できる項目は3つまでとなるため、4項目目の単位は削除する。
      if (activeComparison && graphUnits.length > 3) {
        graphUnits.pop();
      }
      //比較データは3項目までの表示となるため、4項目目は削除する。
      if (comparisonLineGraph.length > 3) {
        comparisonLineGraph.pop();
      }

      /* グラフ表示用データ */
      const totalLineGraph = [...lineGraph];
      //日付取得範囲が１日かつ比較状態の場合、グラフ表示用データに比較データを追加
      if (dateRange === 'day' && activeComparison) {
        totalLineGraph.push(...comparisonLineGraph);
      }
      return (
        <PolygonalLineGraph
          xLabel={xAxisLabel}
          comparisonXLabel={comparisonXAxisLabel}
          yTitle={yTitle}
          csvFileNameDate={
            activeComparison
              ? `${csvFileNameDate},${comparisonCsvFileNameDate}`
              : csvFileNameDate
          } //csvファイル名の日付(グラフタイトルとは表示形式が異なる)
          csvFileNameTarget={selectCsvTitleNameTarget} //グラフタイトルとcsvファイル名
          graph={totalLineGraph}
          comparisonGraph={comparisonLineGraph}
          graphLegend={graphLegend}
          graphUnits={graphUnits}
          activeComparison={activeComparison}
          graphTarget={graphTarget}
          dateRange={dateRange}
        />
      );
    } else if (graphType === 'bar') {
      //棒グラフの場合
      if (barGraphDataHot.length === 0) return <></>;
      if (activeComparison) {
        //比較状態の場合
        //グラフ描画対象によってBarChartGraphへ渡すデータを切り替える
        switch (graphTarget) {
          //Qtonの場合
          case 'qton':
          default:
            return (
              <BarChartGraph
                graphMode="Aggregation"
                graphTarget={graphTarget}
                xLabel={xAxisLabel}
                dateRange={dateRange}
                csvFileNameDate={`${csvFileNameDate},${comparisonCsvFileNameDate}`}
                csvFileNameTarget={graphTitle}
                barGraphData1={barGraphDataHot}
                comparisonBarGraphData1={comparisonBarGraphDataHot}
              />
            );
          case 'hyozan':
            return (
              <BarChartGraph
                graphMode="Aggregation"
                graphTarget={graphTarget}
                xLabel={xAxisLabel}
                dateRange={dateRange}
                csvFileNameDate={`${csvFileNameDate},${comparisonCsvFileNameDate}`}
                csvFileNameTarget={graphTitle}
                barGraphData1={barGraphDataCool}
                comparisonBarGraphData1={comparisonBarGraphDataCool}
              />
            );
          case 'chiller':
            return (
              <BarChartGraph
                graphMode="Aggregation"
                graphTarget={graphTarget}
                xLabel={xAxisLabel}
                dateRange={dateRange}
                csvFileNameDate={`${csvFileNameDate},${comparisonCsvFileNameDate}`}
                csvFileNameTarget={graphTitle}
                barGraphData1={barGraphDataCool}
                barGraphData2={barGraphDataHot}
                comparisonBarGraphData1={comparisonBarGraphDataCool}
                comparisonBarGraphData2={comparisonBarGraphDataHot}
              />
            );
        }
      }

      //比較状態でない場合
      //グラフ描画対象によってBarChartGraphへ渡すデータを切り替える
      switch (graphTarget) {
        //Qtonの場合
        case 'qton':
        default:
          return (
            <BarChartGraph
              graphMode="Aggregation"
              graphTarget={graphTarget}
              xLabel={xAxisLabel}
              dateRange={dateRange}
              csvFileNameDate={csvFileNameDate}
              csvFileNameTarget={graphTitle}
              barGraphData1={barGraphDataHot}
            />
          );
        case 'hyozan':
          return (
            <BarChartGraph
              graphMode="Aggregation"
              graphTarget={graphTarget}
              xLabel={xAxisLabel}
              dateRange={dateRange}
              csvFileNameDate={csvFileNameDate}
              csvFileNameTarget={graphTitle}
              barGraphData1={barGraphDataCool}
            />
          );
        case 'chiller':
          return (
            <BarChartGraph
              graphMode="Aggregation"
              graphTarget={graphTarget}
              xLabel={xAxisLabel}
              dateRange={dateRange}
              csvFileNameDate={csvFileNameDate}
              csvFileNameTarget={graphTitle}
              barGraphData1={barGraphDataCool}
              barGraphData2={barGraphDataHot}
            />
          );
      }
    } else if (graphType === 'barLineMixed') {
      //棒・折れ線混合グラフの場合
      if (energyGraphData.length === 0 && copGraphData.length === 0) {
        return <></>;
      }
      //比較判定が無効の場合や比較データがない場合は通常のグラフを表示する
      if (
        activeComparison === false ||
        (comparisonEnergyGraphData.length === 0 &&
          comparisonCopEnergyGraphData.length === 0)
      ) {
        return (
          <BarLineMixedChartGraph
            graphTarget={graphTarget}
            xLabel={xAxisLabel}
            dateRange={dateRange}
            csvFileNameDate={csvFileNameDate}
            csvFileNameTarget={graphTitle}
            barGraphData={energyGraphData}
            lineGraphData={copGraphData}
          />
        );
      }

      return (
        <BarLineMixedChartGraph
          graphTarget={graphTarget}
          xLabel={xAxisLabel}
          dateRange={dateRange}
          csvFileNameDate={`${csvFileNameDate},${comparisonCsvFileNameDate}`}
          csvFileNameTarget={graphTitle}
          barGraphData={energyGraphData}
          lineGraphData={copGraphData}
          comparisonBarGraphData={comparisonEnergyGraphData}
          comparisonLineGraphData={comparisonCopEnergyGraphData}
        />
      );
    }
    return <></>;
  };

  return (
    <>
      <Card>
        <CardContent className="graph__card__main--otherUnit">
          {/* 消費電力とトレンドグラフの選択 */}
          {/* Qton,ChillerはタブにPower ConsumptionとTrend Reportingを含むのでRTGraphではブランクとする */}
          {graphTarget === 'hyozan' && (
            <PowerTrendSelectButton
              graphType={graphType}
              selectPowerTrend={selectPowerTrend}
            />
          )}
          {/* グラフデータ設定欄 */}
          {timeZone !== '' && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="graph__card__main__menu">
              <Grid item xs="auto" container alignItems="center" spacing={2}>
                {/* カレンダー */}
                <Grid item xs="auto">
                  <StaticDatePicker
                    setGraphDisplayDate={setGraphDispDate}
                    timeZone={timeZone}
                    dateRange={dateRange}
                  />
                </Grid>
                {/* 比較用カレンダー */}
                <Grid item xs>
                  {activeComparison ? (
                    <StaticComparisonDatePicker
                      setGraphDisplayDate={setComparisonGraphDispDate}
                      timeZone={timeZone}
                      dateRange={dateRange}
                      onCloseClick={handleOnComparison}
                    />
                  ) : (
                    dateRange !== 'tenYear' && (
                      //比較用カレンダー表示ボタン
                      <AddButton
                        onClick={handleOnComparison}
                        buttonText={trans('SiteGraph.buttonTxt.comparison')}
                      />
                    )
                  )}
                </Grid>
              </Grid>
              <Grid item xs="auto" container alignItems="center" spacing={3}>
                {/* 表示する状態値の選択 */}
                {graphType === 'line' && (
                  <Grid item xs="auto">
                    <PrimaryButton
                      onClick={() => setIsDialogOpen(true)}
                      buttonText={trans('RTGraph.line.buttonTxt.graphItem')}
                    />
                  </Grid>
                )}
                {/* 折れ線グラフ表示機器切り替え */}
                {timeZone !== '' &&
                  graphType === 'line' &&
                  graphTarget === 'qton' &&
                  childUnitInfo !== undefined &&
                  childUnitInfo.length !== 0 && (
                    <Grid item xs="auto">
                      <PrimaryButton
                        onClick={() => setIsUnitSelectDialogOpen(true)}
                        buttonText={trans('RTGraph.line.buttonTxt.graphUnit')}
                      />
                    </Grid>
                  )}
                <Grid item xs="auto">
                  {/* グラフ表示期間設定セレクトボックス */}
                  {graphType === 'line' ? (
                    <TextFieldSelectBox
                      label={trans('RTGraph.txtField.label.graphRange')}
                      choices={pullDownMenu.LINE_GRAPH_RANGE}
                      value={dateRange}
                      setValue={setDateRange}
                      id={'graphRangeSelect'}
                      class="graph__card__main__menu__selectBox"
                    />
                  ) : (
                    <TextFieldSelectBox
                      label={trans('RTGraph.txtField.label.graphRange')}
                      choices={pullDownMenu.GRAPH_RANGE}
                      value={dateRange}
                      setValue={setDateRange}
                      id={'graphRangeSelect'}
                      class="graph__card__main__menu__selectBox"
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          {childUnitInfo?.length !== 0 && (
            <Grid container justifyContent="center" alignItems="flex-end">
              {/* ユニット名を表示 */}
              <Grid item xs="auto" className="graph__card__main__unitName">
                {selectCsvTitleNameTarget}
              </Grid>
            </Grid>
          )}
          {/* グラフ */}
          {apiWait || comparisonApiWait ? (
            <Stack justifyContent="center" alignItems="center">
              {graphDispError || comparisonGraphDispError ? (
                <h1 className="graph__card__main__errorMessage">
                  {trans('RTGraph.errMsg.gettingDataFailure')}
                </h1>
              ) : (
                <Box className="graph__card__main__progress">
                  <LinearWithValueLabel />
                </Box>
              )}
            </Stack>
          ) : graphDispError || comparisonGraphDispError ? (
            <Stack justifyContent="center" alignItems="center">
              <h1 className="graph__card__main__errorMessage">
                {trans('RTGraph.errMsg.gettingDataFailure')}
              </h1>
            </Stack>
          ) : (
            <GraphDisplay />
          )}
        </CardContent>
      </Card>
      {graphTarget === 'qton' ? (
        <>
          <ItemGraphDialogQton
            activeComparison={activeComparison}
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
          />
          <ChangeUnitGraphDialog
            isDialogOpen={isUnitSelectDialogOpen}
            setIsDialogOpen={setIsUnitSelectDialogOpen}
            parentUnitID={graphTargetID}
            parentUnitName={graphTitle}
            childUnitInfo={childUnitInfo}
            displayUnitID={selectGraphTargetID}
            displayUnitName={selectCsvTitleNameTarget}
            setDisplayUnitID={setSelectGraphTargetID}
            setDisplayUnitName={setSelectCsvTitleNameTarget}
          />
        </>
      ) : graphTarget === 'hyozan' ? (
        <ItemGraphDialogHyozan
          activeComparison={activeComparison}
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        />
      ) : graphTarget === 'chiller' ? (
        <ItemGraphDialogChiller
          activeComparison={activeComparison}
          isDialogOpen={isDialogOpen}
          setIsDialogOpen={setIsDialogOpen}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        />
      ) : (
        <></>
      )}
    </>
  );
});
export default RTGraph;
